import { Controller } from '@hotwired/stimulus'
import { gsap } from 'gsap'
import { debounce } from 'lodash'

export default class extends Controller {
  static values = {
    direction: { type: String, default: 'left' },
    directionMobile: { type: String, default: '' },
    speed: { type: Number, default: 0.5 },
  }

  static RESIZE_DEBOUNCE = 250

  static PIXELS_PER_SECOND = 50

  connect() {
    this.initializeTicker()
    this.bindResizeHandler()
  }

  disconnect() {
    this.removeResizeHandler()
    this.cleanup()
  }

  initializeTicker() {
    if (getComputedStyle(this.element).display === 'none' || this.element.hidden) return

    this.storeOriginalStyles()
    const dimensions = this.prepareContent()
    this.createClones(dimensions.cloneCount)
    this.animate(dimensions)
  }

  storeOriginalStyles() {
    if (!this.element._originalWidth) {
      this.element._originalWidth = this.element.style.width
    }
  }

  prepareContent() {
    const content = this.getContent()
    const direction = this.getCurrentDirection()

    this.element.style.width = '10000px'
    if (!this.hasTickerContent()) {
      content.style.display = 'inline-block'
    }

    const gap = this.constructor.getContentGap(content)
    const width = content.offsetWidth
    const height = content.offsetHeight
    const cloneCount = this.constructor.calculateCloneCount(width, height)

    this.element.style.width = `${width * (cloneCount + 1)}px`

    if (this.constructor.isVerticalDirection(direction)) {
      this.element.style.height = `${height * (cloneCount + 1)}px`
    } else {
      this.element.style.removeProperty('height')
    }

    return {
      width, height, cloneCount, gap,
    }
  }

  static getContentGap(content) {
    const styles = getComputedStyle(content)
    const gap = parseFloat(styles.rowGap) || parseFloat(styles.columnGap) || parseFloat(styles.gap)
    return Number.isFinite(gap) ? gap : 0
  }

  getContent() {
    return this.element.querySelector('.ticker-content') || this.element.children[0]
  }

  hasTickerContent() {
    return !!this.element.querySelector('.ticker-content')
  }

  getCurrentDirection() {
    return window.innerWidth < 768
      ? (this.directionMobileValue || this.directionValue)
      : this.directionValue
  }

  static isVerticalDirection(direction) {
    return direction === 'up' || direction === 'down'
  }

  static calculateCloneCount(width, height) {
    return Math.ceil(Math.max(window.innerWidth / width, window.innerHeight / height)) + 2
  }

  createClones(cloneCount) {
    const clones = []
    for (let i = 0; i < cloneCount; i++) {
      clones.push(this.cloneContent())
    }
    this.element._clones = clones
  }

  cloneContent() {
    const tickerContent = this.element.querySelector('.ticker-content')

    if (!tickerContent) {
      return this.cloneSimpleContent()
    }

    return this.constructor.cloneTickerContent(tickerContent)
  }

  cloneSimpleContent() {
    const originalContent = this.element.children[0]
    const clone = originalContent.cloneNode(true)
    this.element.insertBefore(clone, originalContent)
    return clone
  }

  static cloneTickerContent(tickerContent) {
    const originalChildren = Array.from(tickerContent.children).filter(
      (child) => !child.hasAttribute('data-ticker-clone'),
    )

    return originalChildren.map((child) => {
      const clone = child.cloneNode(true)
      clone.setAttribute('data-ticker-clone', 'true')
      tickerContent.appendChild(clone)
      return clone
    })
  }

  animate(dimensions) {
    const direction = this.getCurrentDirection()
    const isVertical = this.constructor.isVerticalDirection(direction)
    const isReverse = direction === 'right' || direction === 'up'
    const baseSize = isVertical ? dimensions.height : dimensions.width
    const distance = baseSize + (dimensions.gap || 0)
    const duration = distance / this.constructor.PIXELS_PER_SECOND / this.speedValue

    const { from, to } = this.constructor.buildAnimationProps(
      isVertical,
      isReverse,
      distance,
      duration,
    )

    this.element._animation = gsap.fromTo(
      this.element,
      from,
      {
        ...to,
        repeat: -1,
        ease: 'none',
        immediateRender: false,
      },
    )
  }

  static buildAnimationProps(isVertical, isReverse, distance, duration) {
    const axis = isVertical ? 'y' : 'x'
    const fromValue = isReverse ? -distance : 0
    const toValue = isReverse ? 0 : -distance

    return {
      from: { [axis]: fromValue },
      to: { [axis]: toValue, duration },
    }
  }

  bindResizeHandler() {
    this.debouncedRefresh = debounce(() => this.refresh(), this.constructor.RESIZE_DEBOUNCE)
    window.addEventListener('resize', this.debouncedRefresh)
  }

  removeResizeHandler() {
    if (this.debouncedRefresh) {
      this.debouncedRefresh.cancel()
      window.removeEventListener('resize', this.debouncedRefresh)
    }
  }

  refresh() {
    this.cleanup()
    requestAnimationFrame(() => this.initializeTicker())
  }

  cleanup() {
    this.cleanupAnimation()
    this.cleanupClones()
    this.restoreOriginalStyles()
  }

  cleanupAnimation() {
    if (this.element._animation) {
      this.element._animation.kill()
      delete this.element._animation
    }
  }

  cleanupClones() {
    if (this.element._clones) {
      this.element._clones.forEach((clone) => this.removeClone(clone))
      delete this.element._clones
    }

    this.removeTickerClones()
    this.removeExtraContent()
  }

  removeClone(clone) {
    if (Array.isArray(clone)) {
      clone.forEach((cloneChild) => this.constructor.removeElement(cloneChild))
    } else {
      this.constructor.removeElement(clone)
    }
  }

  static removeElement(element) {
    if (element?.parentNode) {
      element.parentNode.removeChild(element)
    }
  }

  removeTickerClones() {
    const tickerContent = this.element.querySelector('.ticker-content')
    if (tickerContent) {
      const clonedElements = tickerContent.querySelectorAll('[data-ticker-clone]')
      clonedElements.forEach((cloned) => this.constructor.removeElement(cloned))
    }
  }

  removeExtraContent() {
    if (!this.hasTickerContent()) {
      const originalContent = this.element.children[0]
      while (this.element.children.length > 1) {
        const lastChild = this.element.children[this.element.children.length - 1]
        if (lastChild !== originalContent) {
          this.element.removeChild(lastChild)
        } else {
          break
        }
      }
    }
  }

  restoreOriginalStyles() {
    if (this.element._originalWidth) {
      this.element.style.width = this.element._originalWidth
    } else {
      this.element.style.removeProperty('width')
    }
    this.element.style.removeProperty('height')
  }
}

